import { styled } from "@linaria/react";
import { useAvivaSelector } from "@redux/hook";
import { SCREEN_SIZE } from "@static/screen-size";
import { Helpers } from "@utils/helpers";
import React, { forwardRef } from "react";
import { useHistory } from "react-router-dom";
import { ITheme, useTheme } from "src/theme";

const StepperContainerWrapper = styled.div<{ backgroundColor?: string }>`
  background-color: #fff;
  top: 60px;
  left: 0;
  right: 0;
  z-index: 10;
  position: absolute;
  
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    position: fixed;
    top: 0;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    right: 24px;
    left: unset;
    top: 200px;
    background-color: transparent; 
  }
`;
const StepperLinkVisit = styled.div<{
  backgroundColor?: string;
  height: string;
  totalItems: number;
  currentItem: number;
}>`
  position: absolute;
  bottom: 8px;
  height: 2px;
  background-color: ${(props) =>
    props.backgroundColor ? props.backgroundColor : "#F5F5F5"};
  z-index: 0;
  left: 6%;
  width: ${({ totalItems, currentItem }) =>
    `calc((100% / ${totalItems} + 6px) * ${currentItem})`};
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    top: 0;
    height: ${(props) => (props.backgroundColor ? props.height : "0px")};
    right: 11px;
    width: 2px;
    left: unset;
    background-color: ${(props) =>
      props.backgroundColor ? props.backgroundColor : "#fff"};
  }
`;

const StepperLink = styled.div<{ backgroundColor?: string }>`
  position: absolute;
  left: 4px;
  right: 4px;
  bottom: 8px;
  height: 2px;
  background-color: #f5f5f5;
  z-index: 0;
  left: 6%;
  right: 6%;

  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    top: 0;
    height: 100%;
    right: 11px;
    width: 2px;
    left: unset;
    background-color: #fff;
  }
`;

const StepperContainer = styled.div<StepperProps>`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: unset;
  padding: 16px 24px;
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    padding: 100px 0 16px;
    gap: 24px 0;
    width: 100%;
    max-width: 588px;
    margin: auto;
    z-index: unset;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    padding: 75px 16px 16px 16px;
    gap: 24px 0;
    flex-direction: column;
    width: auto;
    padding: 0px;
    width: 100%;
    z-index: unset;
  }
`;

const StepContainer = styled.div<
  Partial<StepItemProps> & { isClickable: boolean }
>`
  cursor: ${(props) => (props.isClickable ? "pointer" : "default")};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px 4px;
  z-index: 1;
  flex: 0 0 65px;
  height: 68px;
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    flex-direction: row;
    justify-content: flex-end;
    flex: unset;
    height: unset;
  }
`;

const StepText = styled.p<{ active?: boolean }>`
  font-size: 12px;
  color: ${(props) => (props.active ? "#434343" : "#8C8C8C")};
  font-weight: ${(props) => (props.active ? "600" : "400")};
  line-height: 140%;
  max-width: 65px;
  text-align: center;
  margin: auto;
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    text-align: right;
    max-width: unset;
    font-size: 16px;
    margin: unset;
  }
`;

const StepBullet = styled.div<StepItemProps>`
  margin-top: auto;
  width: 18px;
  height: 18px;
  border-radius: 100rem;
  background-color: ${(props) =>
    props.active
      ? "#883E89"
      : props.index < props.activeStepIndex
      ? "#DFC2DD"
      : props.backgroundColor
      ? props.backgroundColor
      : "#F5F5F5"};
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    width: 24px;
    height: 24px;
    margin-left: 8px;
    background-color: ${(props) =>
      props.active
        ? "#883E89"
        : props.index < props.activeStepIndex
        ? "#DFC2DD"
        : props.backgroundColor
        ? props.backgroundColor
        : "#fff"};
  }
`;

const StepInnerContainer = styled.div`
  display: flex;
  margin: auto;
  justify-content: center;
  position: relative;
  gap: 4px;
  @media screen and (min-width: ${SCREEN_SIZE.tabletsUp}px) {
    justify-content: space-between;
    width: 100%;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    position: unset;
    display: grid;
    gap: 24px;
    width: unset;
  }
`;

export interface Step {
  id: string;
  label: string;
  path?: string;
}

export interface StepItemProps {
  active: boolean;
  index: number;
  activeStepIndex: number;
  backgroundColor?: string;
}
export interface StepperProps {
  steps: Step[];
  backgroundColor?: string;
  theme?: ITheme;
  activeId?: string;
}

const Stepper = forwardRef<HTMLDivElement, StepperProps>(
  ({ steps, backgroundColor, activeId, ...props }: StepperProps, ref) => {
    const theme = useTheme();
    const history = useHistory();
    const campaignDetail = useAvivaSelector(
      (state) => state.loading.campaignDetail
    );
    const analyticUrlParams = useAvivaSelector(
      (state) => state.urlAnalytic.param
    );
    const paramCC = campaignDetail?.campaignCode
      ? `?cc=${campaignDetail.campaignCode}`
      : "";
    const analyticParam = Helpers.getUrlAnalyticParams(
      paramCC !== "",
      analyticUrlParams
    );

    const activeStepIndex = steps.reduce((result, step, index) => {
      if (step.id === activeId) return (result = index);
      return result;
    }, 0);

    const activeStep = steps.findIndex((step) => step.id === activeId);

    return (
      <StepperContainerWrapper>
        <StepperContainer theme={theme} steps={steps}>
          <StepInnerContainer>
            <StepperLink></StepperLink>
            {steps.map((step, index) => (
              <>
                <StepperLinkVisit
                  backgroundColor={activeStep <= index ? "#DFC2DD" : ""}
                  height={`${activeStep * 50}px`}
                  totalItems={steps.length}
                  currentItem={activeStep}
                ></StepperLinkVisit>
                <StepContainer
                  isClickable={index < activeStepIndex}
                  key={step.label}
                  onClick={() => {
                    if (step?.path && index < activeStepIndex) {
                      history.push(step.path + paramCC + analyticParam);
                    }
                  }}
                >
                  <StepText active={step.id === activeId}>
                    {step.label}
                  </StepText>
                  <StepBullet
                    active={step.id === activeId}
                    index={index}
                    activeStepIndex={activeStepIndex}
                  />
                </StepContainer>
              </>
            ))}
          </StepInnerContainer>
        </StepperContainer>
      </StepperContainerWrapper>
    );
  }
);

Stepper.defaultProps = {
  steps: [],
};
export default Stepper;
